/**
 * acm-self panels — Veřejný přístup k control-plane ACM (ne k appkám na ACM).
 *
 * services  = NodeServicesCard (acm-self.services_state / service_public / service_sso)
 * acl       = admin ACL kdo smí k publikovanému ACM
 * domains   = base_domain platform overrides
 * security  = master key rotate
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Label,
  Modal,
  SearchInput,
  Select,
} from '../../../components/ui';
import { NodeServicesCard } from '../../../components/NodeServicesCard';
import { Fail2banReactionCard } from '../../../components/ui/Fail2banReactionToggle';
import { CodableAccessCard } from '../../../components/CodableAccessCard';
import {
  AlertTriangle,
  Copy,
  Globe,
  Hash,
  Key,
  KeyRound,
  Shield,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { themedConfirm } from '../../../lib/themed-dialogs';

let _widgetScope: EffectiveScope = { node: null, identity: null };
export function setAcmSelfWidgetScope(scope: EffectiveScope) {
  _widgetScope = scope;
}
function callMcp<T = unknown>(name: string, args: Record<string, unknown> = {}) {
  return callWidgetTool(name, args, _widgetScope) as Promise<T>;
}

// ── Domains ─────────────────────────────────────────────────────────────────

function DomainListEditor({
  label,
  list,
  onChange,
  onSave,
  onDelete,
  saving,
}: {
  label: string;
  list: string[];
  onChange: (l: string[]) => void;
  onSave: () => void;
  onDelete: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const items = list.length > 0 ? list : [''];
  return (
    <div className='space-y-1.5 p-2 border border-gray-800 rounded'>
      <div className='flex items-center justify-between'>
        <span className='text-xs text-gray-400 font-mono'>{label}</span>
        <div className='flex gap-1'>
          <Button variant='ghost' size='sm' onClick={() => onChange([...items, ''])} title={t('acmSelf.addFallback')}>
            + fallback
          </Button>
          <Button variant='primary' size='sm' onClick={onSave} loading={saving}>{t('common.save')}</Button>
          <Button variant='ghost' size='sm' onClick={onDelete} title={t('acmSelf.deleteAllTitle')}>×</Button>
        </div>
      </div>
      {items.map((d, idx) => (
        <div key={idx} className='flex items-center gap-2'>
          <span className='text-[10px] text-gray-600 w-16'>{idx === 0 ? 'primary' : `fallback ${idx}`}</span>
          <input
            type='text'
            value={d}
            onChange={(e) => {
              const next = [...items];
              next[idx] = e.target.value;
              onChange(next);
            }}
            placeholder={idx === 0 ? 'services.apollo-it.cz' : t('acmSelf.optionalFallback')}
            className='flex-1 bg-gray-950 border border-gray-800 rounded px-2 py-1 text-xs text-white font-mono'
          />
          {idx > 0 && (
            <button
              type='button'
              onClick={() => onChange(items.filter((_, i) => i !== idx))}
              className='text-gray-600 hover:text-red-400 text-xs'
              title={t('acmSelf.removeFallback')}
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function DomainsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['platform-config', _widgetScope.node ?? 'local'],
    queryFn: () =>
      callMcp<{
        ok: boolean;
        base_domain: Record<string, string[]>;
        effective: string[];
      }>('acm-self.platform_domains', { action: 'get' }),
  });

  const [domains, setDomains] = useState<Record<string, string[]>>({});

  if (data && Object.keys(domains).length === 0 && Object.keys(data.base_domain ?? {}).length > 0) {
    setDomains({ ...data.base_domain });
  }

  function getList(module: string): string[] {
    return domains[module] ?? [];
  }
  function setList(module: string, list: string[]) {
    setDomains({ ...domains, [module]: list });
  }

  const saveMut = useMutation({
    mutationFn: ({ module, base_domain }: { module: string; base_domain: string[] }) =>
      callMcp('acm-self.platform_domains', { action: 'set', module, base_domain }),
    onSuccess: () => {
      toast.success(t('acmSelf.domainSaved'));
      qc.invalidateQueries({ queryKey: ['platform-config'] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const deleteMut = useMutation({
    mutationFn: (module: string) => callMcp('acm-self.platform_domains', { action: 'delete', module }),
    onSuccess: () => {
      toast.success(t('acmSelf.overrideDeleted'));
      qc.invalidateQueries({ queryKey: ['platform-config'] });
    },
  });

  const KNOWN_MODULES = [
    'supabase', 'llm-profiles', 'fleet', 'acm-self', 'proxmox', 'apphost',
    'openhands', 'orchestrator', 'websearch', 'sso',
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle icon={Globe}>{t('acmSelf.domainsTitle')}</CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        {isLoading
          ? <div className='text-xs text-gray-500'>{t('common.loading')}</div>
          : (
            <>
              <div className='text-xs text-gray-400 bg-gray-900/40 border border-gray-800 rounded p-3 space-y-1'>
                <div>
                  {t('acmSelf.currentEffective')}{' '}
                  <code className='text-emerald-400'>{(data?.effective ?? []).join(' → ')}</code>
                </div>
                <div className='text-[10px] text-gray-600'>
                  {t('acmSelf.overrideHelpPrefix')} <code>ACM_BASE_DOMAIN</code> {t('acmSelf.or')}{' '}
                  <code>services.maxcloud.cz</code>.
                </div>
              </div>
              <DomainListEditor
                label={t('acmSelf.globalLabel')}
                list={getList('_platform')}
                onChange={(l) => setList('_platform', l)}
                onSave={() =>
                  saveMut.mutate({
                    module: '_platform',
                    base_domain: getList('_platform').filter(Boolean),
                  })}
                onDelete={() => {
                  setList('_platform', []);
                  deleteMut.mutate('_platform');
                }}
                saving={saveMut.isPending}
              />
              <div className='space-y-3'>
                <div className='text-xs text-gray-400'>Per-module overrides</div>
                {KNOWN_MODULES.map((m) => (
                  <DomainListEditor
                    key={m}
                    label={m}
                    list={getList(m)}
                    onChange={(l) => setList(m, l)}
                    onSave={() =>
                      saveMut.mutate({ module: m, base_domain: getList(m).filter(Boolean) })}
                    onDelete={() => {
                      setList(m, []);
                      deleteMut.mutate(m);
                    }}
                    saving={saveMut.isPending}
                  />
                ))}
              </div>
            </>
          )}
      </CardContent>
    </Card>
  );
}

// ── Admin ACL (přístup k funkcím ACM) ───────────────────────────────────────

interface AclEntry {
  id: number;
  keycloak_sub: string;
  keycloak_username: string | null;
  role: string;
  granted_at: string;
}

interface PublishStatus {
  ok: boolean;
  hash: string;
  base_domain: string;
  admin_acl_count: number;
}

function AclTab() {
  const { t, i18n } = useTranslation();
  const [aclOpen, setAclOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<
    { id: string; username: string; email: string | null } | null
  >(null);
  const [role, setRole] = useState<'admin' | 'operator' | 'viewer'>('viewer');

  const { data: status } = useQuery<PublishStatus>({
    queryKey: ['acm-self-status', _widgetScope.node ?? 'local'],
    queryFn: () => callMcp<PublishStatus>('acm_self_status'),
  });

  const { data: aclData, isLoading: aclLoading, refetch: refetchAcl } = useQuery<{
    ok: boolean;
    acl: AclEntry[];
  }>({
    queryKey: ['acm-self-acl', _widgetScope.node ?? 'local'],
    queryFn: () => callMcp<{ ok: boolean; acl: AclEntry[] }>('acm_self_acl_list'),
  });
  const acl = aclData?.acl ?? [];

  const filterQ = filter.trim().toLowerCase();
  const filteredAcl = filterQ
    ? acl.filter((e) => {
      const name = (e.keycloak_username ?? '').toLowerCase();
      const sub = e.keycloak_sub.toLowerCase();
      const r = e.role.toLowerCase();
      return name.includes(filterQ) || sub.includes(filterQ) || r.includes(filterQ);
    })
    : acl;

  const { data: kcUsersData, isFetching: kcSearching } = useQuery<
    { ok: boolean; users: Array<{ id: string; username: string; email?: string }> }
  >({
    queryKey: ['kc-users-acm', userSearch],
    queryFn: () =>
      callMcp<{ ok: boolean; users: Array<{ id: string; username: string; email?: string }> }>(
        'sso.keycloak_list_users',
        { search: userSearch || undefined, max: 50 },
      ),
    enabled: aclOpen,
    staleTime: 5000,
  });
  const kcUsers = kcUsersData?.users ?? [];

  const aclAdd = useMutation({
    mutationFn: (b: { keycloak_sub: string; keycloak_username?: string; role: string }) =>
      callMcp('acm_self_acl_grant', b),
    onSuccess: () => {
      toast.success(t('systemSettings.added'));
      setAclOpen(false);
      setSelectedUser(null);
      setUserSearch('');
      setRole('viewer');
      refetchAcl();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const aclDel = useMutation({
    mutationFn: (sub: string) => callMcp('acm_self_acl_revoke', { keycloak_sub: sub }),
    onSuccess: () => {
      toast.success(t('systemSettings.removed'));
      refetchAcl();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const roleBadge = (r: string): 'error' | 'warning' | 'secondary' => {
    if (r === 'admin') return 'error';
    if (r === 'operator') return 'warning';
    return 'secondary';
  };

  const roleLabel = (r: string) => {
    if (r === 'admin') return t('systemSettings.roleAdminShort', 'Admin');
    if (r === 'operator') return t('systemSettings.roleOperatorShort', 'Operator');
    if (r === 'viewer') return t('systemSettings.roleViewerShort', 'Viewer');
    return r;
  };

  const formatGranted = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(i18n.language || undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  const openAdd = () => {
    setSelectedUser(null);
    setUserSearch('');
    setRole('viewer');
    setAclOpen(true);
  };

  const closeAdd = () => {
    setAclOpen(false);
    setSelectedUser(null);
    setUserSearch('');
  };

  const removeUser = async (e: AclEntry) => {
    if (
      await themedConfirm(
        t('systemSettings.confirmRemoveUser', {
          user: e.keycloak_username ?? e.keycloak_sub.slice(0, 8),
        }),
      )
    ) {
      aclDel.mutate(e.keycloak_sub);
    }
  };

  const addButton = (
    <Button
      variant='primary'
      size='sm'
      className='shrink-0'
      icon={<UserPlus size={14} />}
      onClick={openAdd}
    >
      {t('systemSettings.add')}
    </Button>
  );

  return (
    <div className='space-y-4'>
      {status?.hash && (
        <div className='flex items-start gap-2.5 rounded-xl border border-blue-500/20 bg-blue-500/5 px-3.5 py-2.5 text-xs text-blue-200/90'>
          <Hash size={14} className='mt-0.5 shrink-0 text-blue-400' />
          <div className='min-w-0 space-y-0.5'>
            <div>
              <span className='text-blue-300/80'>{t('systemSettings.instanceHash')}</span>{' '}
              <code className='font-mono text-blue-100 bg-black/25 px-1.5 py-0.5 rounded'>
                {status.hash}
              </code>
            </div>
            <div className='text-[11px] text-blue-300/60 break-all'>
              {status.hash}.acm[/-api/-agents/-mqtt].{status.base_domain}
            </div>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className='gap-3'>
          <div className='min-w-0 flex-1'>
            <CardTitle icon={Shield}>
              {t('systemSettings.adminAcl', { count: acl.length })}
            </CardTitle>
            <CardDescription>
              {t(
                'systemSettings.adminAclDesc',
                'Kdo smí do dashboardu přes Keycloak — role řídí rozsah oprávnění.',
              )}
            </CardDescription>
          </div>
          {addButton}
        </CardHeader>

        <CardContent className='space-y-3'>
          {acl.length > 0 && (
            <SearchInput
              value={filter}
              onChange={setFilter}
              placeholder={t(
                'systemSettings.aclSearchPlaceholder',
                'Filtrovat uživatele, sub, roli…',
              )}
            />
          )}

          {aclLoading
            ? (
              <div className='py-10 text-center text-xs text-gray-500'>
                {t('common.loading', 'Načítám…')}
              </div>
            )
            : acl.length === 0
            ? (
              <EmptyState
                icon={Users}
                title={t('systemSettings.noAdminUsersShort', 'Žádní admin uživatelé')}
                description={t('systemSettings.noAdminUsers')}
                action={addButton}
              />
            )
            : filteredAcl.length === 0
            ? (
              <EmptyState
                icon={Users}
                title={t('systemSettings.aclNoMatch', 'Nic neodpovídá filtru')}
                description={t(
                  'systemSettings.aclNoMatchDesc',
                  'Zkus jiné jméno, sub nebo roli.',
                )}
              />
            )
            : (
              <>
                {/* Mobile cards */}
                <div className='md:hidden space-y-2'>
                  {filteredAcl.map((e) => (
                    <div
                      key={e.id}
                      className='rounded-xl border border-gray-800 bg-gray-950/40 p-3 space-y-2'
                    >
                      <div className='flex items-start justify-between gap-2'>
                        <div className='min-w-0'>
                          <div className='text-sm font-medium text-gray-100 truncate'>
                            {e.keycloak_username ?? '—'}
                          </div>
                          <div className='font-mono text-[11px] text-gray-500 truncate' title={e.keycloak_sub}>
                            {e.keycloak_sub.slice(0, 12)}…
                          </div>
                        </div>
                        <Badge variant={roleBadge(e.role)}>{roleLabel(e.role)}</Badge>
                      </div>
                      <div className='flex items-center justify-between gap-2'>
                        {e.granted_at
                          ? (
                            <span className='text-[10px] text-gray-600'>
                              {formatGranted(e.granted_at)}
                            </span>
                          )
                          : <span />}
                        <Button
                          variant='ghost'
                          size='sm'
                          className='text-gray-500 hover:text-red-400'
                          onClick={() => removeUser(e)}
                          disabled={aclDel.isPending}
                          title={t('common.delete')}
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table */}
                <div className='hidden md:block overflow-x-auto rounded-xl border border-gray-800/80'>
                  <table className='w-full text-sm'>
                    <thead className='border-b border-gray-800 text-left text-[11px] text-gray-500 uppercase tracking-wider bg-gray-950/40'>
                      <tr>
                        <th className='px-4 py-2.5'>{t('systemSettings.user')}</th>
                        <th className='px-4 py-2.5'>{t('systemSettings.aclSub', 'Sub')}</th>
                        <th className='px-4 py-2.5 w-28'>{t('systemSettings.role')}</th>
                        <th className='px-4 py-2.5 w-36'>
                          {t('systemSettings.aclGrantedAt', 'Přidáno')}
                        </th>
                        <th className='px-4 py-2.5 w-14' />
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-800/50'>
                      {filteredAcl.map((e) => (
                        <tr key={e.id} className='hover:bg-gray-800/15'>
                          <td className='px-4 py-2.5 text-gray-200 font-medium'>
                            {e.keycloak_username ?? '—'}
                          </td>
                          <td
                            className='px-4 py-2.5 font-mono text-xs text-gray-500'
                            title={e.keycloak_sub}
                          >
                            {e.keycloak_sub.slice(0, 8)}…
                          </td>
                          <td className='px-4 py-2.5'>
                            <Badge variant={roleBadge(e.role)}>{roleLabel(e.role)}</Badge>
                          </td>
                          <td className='px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap'>
                            {e.granted_at ? formatGranted(e.granted_at) : '—'}
                          </td>
                          <td className='px-4 py-2.5 text-right'>
                            <Button
                              variant='ghost'
                              size='sm'
                              className='text-gray-600 hover:text-red-400'
                              onClick={() => removeUser(e)}
                              disabled={aclDel.isPending}
                              title={t('common.delete')}
                            >
                              <Trash2 size={13} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
        </CardContent>
      </Card>

      <Modal
        open={aclOpen}
        onClose={closeAdd}
        title={t('systemSettings.addAdmin')}
        footer={
          <>
            <Button variant='secondary' onClick={closeAdd}>
              {t('common.cancel')}
            </Button>
            <Button
              variant='primary'
              onClick={() =>
                selectedUser &&
                aclAdd.mutate({
                  keycloak_sub: selectedUser.id,
                  keycloak_username: selectedUser.username,
                  role,
                })}
              loading={aclAdd.isPending}
              disabled={!selectedUser}
              icon={<UserPlus size={14} />}
            >
              {t('systemSettings.add')}
            </Button>
          </>
        }
      >
        <div className='space-y-4'>
          <p className='text-xs text-gray-500'>
            {t(
              'systemSettings.addAdminHint',
              'Vyber uživatele z Keycloak a přiřaď mu roli pro přístup k ACM dashboardu.',
            )}
          </p>
          <div>
            <Label>{t('systemSettings.searchKeycloakUser')}</Label>
            <div className='mt-1.5'>
              <SearchInput
                value={userSearch}
                onChange={(v) => {
                  setUserSearch(v);
                  setSelectedUser(null);
                }}
                placeholder={t('systemSettings.nameEmailPlaceholder')}
                autoFocus
              />
            </div>
          </div>
          <div>
            <Label>
              {t('systemSettings.user')}{' '}
              <span className='text-gray-600 font-normal'>
                {kcSearching ? t('systemSettings.searching') : `(${kcUsers.length})`}
              </span>
            </Label>
            <Select
              className='mt-1.5 w-full'
              value={selectedUser?.id ?? ''}
              onChange={(e) => {
                const u = kcUsers.find((u) => u.id === e.target.value);
                setSelectedUser(
                  u ? { id: u.id, username: u.username, email: u.email ?? null } : null,
                );
              }}
              size={Math.min(8, Math.max(3, kcUsers.length || 3))}
            >
              {kcUsers.length === 0 && !kcSearching && (
                <option value='' disabled>
                  {t('systemSettings.noUser')}
                </option>
              )}
              {kcUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username}
                  {u.email ? ` — ${u.email}` : ''}
                </option>
              ))}
            </Select>
            {selectedUser && (
              <div className='mt-2 flex items-center gap-2 rounded-lg border border-plex/25 bg-plex/5 px-3 py-2 text-xs'>
                <KeyRound size={12} className='text-plex shrink-0' />
                <span className='text-gray-200 font-medium truncate'>{selectedUser.username}</span>
                {selectedUser.email && (
                  <span className='text-gray-500 truncate'>{selectedUser.email}</span>
                )}
              </div>
            )}
          </div>
          <div>
            <Label>{t('systemSettings.role')}</Label>
            <Select
              className='mt-1.5 w-full'
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'operator' | 'viewer')}
            >
              <option value='admin'>{t('systemSettings.roleAdmin')}</option>
              <option value='operator'>{t('systemSettings.roleOperator')}</option>
              <option value='viewer'>{t('systemSettings.roleViewer')}</option>
            </Select>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Master key ──────────────────────────────────────────────────────────────

function SecurityTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [rotateModalOpen, setRotateModalOpen] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const rotateMut = useMutation({
    mutationFn: async () => {
      const parsed = await callMcp<{
        ok?: boolean;
        error?: string;
        new_key: string;
        backup: string;
        db_rows_updated: number;
      }>('acm-self.master_key_rotate', { confirm: true });
      if (!parsed.ok) throw new Error(parsed.error ?? 'Rotation failed');
      return parsed;
    },
    onSuccess: (data) => {
      setNewKey(data.new_key);
      toast.success(t('acmSelf.rotationOk', { rows: data.db_rows_updated }));
    },
    onError: (e) => toast.error(t('acmSelf.rotationFailed', { message: (e as Error).message })),
  });

  return (
    <div className='space-y-4'>
      <Fail2banReactionCard />
      <Card>
        <CardHeader>
          <CardTitle icon={Key}>{t('acmSelf.masterKeyTitle')}</CardTitle>
          <span className='text-[10px] text-gray-500'>{t('acmSelf.masterKeySubtitle')}</span>
        </CardHeader>
        <CardContent>
          <div className='flex items-center justify-between p-3 rounded-xl border border-amber-500/20 bg-amber-500/5'>
            <div className='flex items-center gap-3'>
              <AlertTriangle size={16} className='text-amber-400' />
              <div>
                <div className='text-sm text-amber-300'>{t('acmSelf.rotateTitle')}</div>
                <div className='text-[10px] text-gray-500'>{t('acmSelf.rotateDesc')}</div>
              </div>
            </div>
            <Button
              variant='secondary'
              size='sm'
              onClick={async () => {
                if (await themedConfirm(t('acmSelf.rotateConfirm'))) {
                  rotateMut.mutate();
                  setRotateModalOpen(true);
                }
              }}
              loading={rotateMut.isPending}
              disabled={rotateMut.isPending}
            >
              <Key size={12} /> {t('acmSelf.rotateButton')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {newKey && (
        <Modal
          open={rotateModalOpen}
          onClose={async () => {
            if (await themedConfirm(t('acmSelf.closeConfirm'))) {
              setRotateModalOpen(false);
              setNewKey(null);
              qc.invalidateQueries({ queryKey: ['acm-self-status'] });
            }
          }}
          title={t('acmSelf.newKeyTitle')}
        >
          <div className='space-y-3'>
            <div className='p-3 rounded-xl border border-red-500/30 bg-red-500/10 text-xs text-red-300'>
              {t('acmSelf.newKeyWarning')}
            </div>
            <div className='flex items-center gap-2 p-3 rounded-xl border border-gray-700 bg-gray-950'>
              <code className='font-mono text-sm text-emerald-400 flex-1 break-all'>{newKey}</code>
              <Button
                variant='secondary'
                size='sm'
                onClick={() => {
                  navigator.clipboard.writeText(newKey).then(() =>
                    toast.success(t('acmSelf.copiedToClipboard'))
                  );
                }}
              >
                <Copy size={12} /> {t('acmSelf.copy')}
              </Button>
            </div>
            <div className='text-[11px] text-gray-400'>
              {t('acmSelf.nextSteps1')}{' '}
              <code className='text-amber-300'>curl -H &quot;x-api-key: …&quot; /api/server-info</code>
              {t('acmSelf.nextSteps2')} <code className='text-amber-300'>credentials.md</code>{' '}
              {t('acmSelf.nextSteps3')}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Services = chybějící panel (NodeServicesCard) ───────────────────────────

function ServicesTab() {
  const { t } = useTranslation();
  return (
    <div className='space-y-4'>
      <div className='text-xs text-gray-500 border border-gray-800 rounded-xl px-3 py-2 bg-gray-900/30'>
        {t(
          'acmSelf.servicesOnlyHint',
          'Jen control-plane ACM (UI / API / MCP / Agent) — ne aplikace běžící na ACM. Publish app je v owner modulech a v Network přehledu.',
        )}
      </div>
      <NodeServicesCard
        nodeName={_widgetScope.node ?? undefined}
        title={t('systemSettings.publicAccessCardTitle')}
      />
      {/* CodAble má vlastní UI a MCP URL — patří sem vedle služeb ACM.
          Když ho uzel nemá, karta nabídne nasazení (Filip 2026-08-17).
          Jen pro lokální uzel: hub_publish/hub_sso se volají lokálně. */}
      {!_widgetScope.node && <CodableAccessCard />}
    </div>
  );
}

// ── Widget wrappers ─────────────────────────────────────────────────────────

export function ServicesPanel({ scope }: Pick<WidgetRenderProps, 'scope'>) {
  setAcmSelfWidgetScope(scope ?? { node: null, identity: null });
  return <ServicesTab />;
}

export function AclPanel({ scope }: Pick<WidgetRenderProps, 'scope'>) {
  setAcmSelfWidgetScope(scope ?? { node: null, identity: null });
  return <AclTab />;
}

export function DomainsPanel({ scope }: WidgetRenderProps) {
  setAcmSelfWidgetScope(scope ?? { node: null, identity: null });
  return <DomainsTab />;
}

export function SecurityPanel({ scope }: WidgetRenderProps) {
  setAcmSelfWidgetScope(scope ?? { node: null, identity: null });
  return <SecurityTab />;
}
